import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { getArrayOfPagesShown } from './getPageInfo';
import { isElementVisible } from '../conditional-fields/evaluate-field-condition';
import { isFieldValid } from '../components/values/Get';
import { CustomPageValidationMap } from '../validators/page/map';

export type PagesAndFields = {
    pageId: string;
    isValid: boolean;
};

type Element = {
    elementId: string;
    isValid: boolean | undefined;
};

export const getPageAndFieldValidity = async (
    formConfig: FormConfig,
    formValues: FormValues
): Promise<PagesAndFields[]> => {
    const pageArray = await getArrayOfPagesShown(formConfig, formValues);

    const pagesAndFields = pageArray.map((pageId) => {
        return {
            pageId,
            isValid: isThisPageValid(formConfig, formValues, pageId) // isPageValid,
        };
    });

    return pagesAndFields;
};

export const getFirstInvalidPageIdBeforeTheCurrentPageId = (
    pagesAndFieldsValidity: PagesAndFields[],
    requestedPageId: string // the pageId passed in the URL
): string | undefined => {
    let firstInvalidPageId = '';
    let firstInvalidPageIndex = 0;

    let requestedPageIndex = 0;

    pagesAndFieldsValidity.forEach(({ pageId, isValid }, pageIndex) => {
        if (pageId === requestedPageId) {
            requestedPageIndex = pageIndex;
        }

        if (!isValid && firstInvalidPageId === '') {
            firstInvalidPageId = pageId;
            firstInvalidPageIndex = pageIndex;
        }
    });

    return firstInvalidPageIndex < requestedPageIndex ? firstInvalidPageId : undefined;
};

export const areAllPagesValid = (pagesAndFieldsValidity: PagesAndFields[]): boolean => {
    return pagesAndFieldsValidity.length > 0 && pagesAndFieldsValidity.every((paf) => paf.isValid);
};

export const isThisPageValid = (formConfig: FormConfig, formValues: FormValues, pageId: string) => {
    const elements: Element[] = [];

    const page = formConfig.pages.find((page) => page.pageId === pageId);

    if (!page) {
        throw new Error('Unknown pageId: ' + pageId);
    }

    page.elements.forEach((element) => {
        const { elementId } = element;

        if (!isElementVisible(formConfig, formValues, elementId)) {
            return;
        }

        const isValid = isFieldValid(formConfig, formValues, elementId);

        elements.push({
            elementId,
            isValid
        });
    });

    // Some pages have multiple elements that need to be cross-checked in order to determine
    // if the page as a hole is valid or not. In such case, a custom page validator function can be used.
    //
    // If there is a custom page validator declared for the page then use it, otherwise
    // check if ALL elements on the page are valid induvidually
    //
    let customPageValidator = page.customValidation ? CustomPageValidationMap[page.customValidation] : undefined;
    const isPageValid = customPageValidator
        ? customPageValidator(formValues, formConfig, pageId)
        : elements.every((element) => {
              return element.isValid;
          });

    return isPageValid;
};
